import { createContext, useContext } from 'react';

/**
 * Theme mechanism for the two-target b2b-client.
 *
 * The SAME DOM primitives render either the PORTAL (cream/editorial, customer
 * school portal) or the CONSOLE (lights-down dark, founder super-admin) look.
 * Panels stay theme-agnostic; the shell wraps its tree in a single
 * <ThemeProvider theme="portal"|"console">. Primitives read the resolved token
 * bag via useTokens() and apply colors/fonts through inline styles (so they are
 * correct regardless of what the Tailwind config defines) and layout through
 * plain Tailwind utility + arbitrary-value classes.
 *
 * NOTE ON THE SCHOOL-ACCENT LAW (portal only): the per-school accentColor is an
 * identity-slot concern (crest, 3px rule, welcome line) owned by the shell, NOT
 * by these primitives. These tokens never expose the school accent on
 * status/data/interactive surfaces. `accent` here is the reserved clay used for
 * focus rings on light, never as a fill.
 */

const FONTS = {
  display: "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif",
  body: "'Hanken Grotesk', system-ui, sans-serif",
  mono: "'IBM Plex Mono', ui-monospace, Menlo, monospace",
};

// Portal control boundary (inputs, selects, pills, toggles). Clears WCAG
// 1.4.11: 3.50:1 on #FFFFFF and 3.34:1 on #FFF9EE. Hairline dividers keep
// `hairline` / `border`.
const PORTAL_CONTROL_BORDER = '#998670';

// PORTAL: cream / editorial. 8/16/24 rhythm; open sections, not cards.
const PORTAL = {
  name: 'portal',
  fonts: FONTS,
  page: '#FFFBF6',
  surface: '#FFF9EE',
  statBg: '#FFFBF6',
  border: '#E7DCCB',
  hairline: '#E8DECE',
  heading: '#2B1B12',
  body: '#3A2A22',
  muted: '#7B665C',
  // Primary button is pill black with cream text (DESIGN.md Components).
  primaryBg: '#111111',
  primaryText: '#FFFBF6',
  primaryBorder: '#111111',
  secondaryBg: 'transparent',
  secondaryText: '#4A2C1D',
  secondaryBorder: '#E7DCCB',
  // Selected chips are sand with ink text and border, so they never read as
  // the primary button.
  pillActiveBg: '#F4E8DA',
  pillActiveText: '#2A1710',
  pillActiveBorder: '#2A1710',
  pillBg: '#FFF9EE',
  pillText: '#3A2A22',
  controlBorder: PORTAL_CONTROL_BORDER,
  pillBorder: PORTAL_CONTROL_BORDER,
  inputBg: '#FFFFFF',
  inputText: '#2B1B12',
  inputBorder: PORTAL_CONTROL_BORDER,
  placeholder: '#7B665C',
  accent: '#E14E2C',
  danger: '#8B3A3A',
  success: '#2D6A3E',
  badgeSandBg: '#F4E8DA',
  // Tailwind focus-ring fragment (brand color, never browser-blue).
  focusRing: 'focus-visible:ring-[#E14E2C] focus-visible:ring-offset-[#FFF9EE]',
  // Portal sections are open hairline-topped blocks, not cards (DESIGN.md
  // rules out a card mosaic on the portal).
  cardClass: 'border-t pt-6 pb-2',
  cardBg: 'transparent',
  switchOn: '#4A2C1D',
  switchOff: '#E7DCCB',
  switchKnob: '#FFFBF6',
  spinnerTrack: '#E7DCCB',
  spinnerHead: '#4A2C1D',
  chart: {
    axis: '#7B665C',
    grid: '#E7DCCB',
    tooltipBg: '#FFF9EE',
    tooltipBorder: '#E7DCCB',
    tooltipText: '#2B1B12',
  },
};

const CONSOLE_SURFACE = '#20130D';

// CONSOLE: lights-down dark, dense. 4/8/12/16 density; compact card radius.
const CONSOLE = {
  name: 'console',
  fonts: FONTS,
  page: '#1B0F0A',
  surface: CONSOLE_SURFACE,
  statBg: '#241611',
  border: '#4A382F',
  hairline: '#4A382F',
  heading: '#F3E7D8',
  body: '#D8C6B6',
  muted: '#B0998A',
  primaryBg: '#F3E7D8',
  primaryText: '#1B0F0A',
  primaryBorder: '#F3E7D8',
  secondaryBg: 'transparent',
  secondaryText: '#F3E7D8',
  secondaryBorder: '#4A382F',
  pillActiveBg: '#3A2A1F',
  pillActiveText: '#F3E7D8',
  pillActiveBorder: '#B0998A',
  pillBg: 'transparent',
  pillText: '#B0998A',
  pillBorder: '#4A382F',
  inputBg: '#1B0F0A',
  inputText: '#F3E7D8',
  inputBorder: '#4A382F',
  controlBorder: '#4A382F',
  placeholder: '#B0998A',
  // Clay is reserved for the active-nav marker in console; focus rings go cream.
  accent: '#E14E2C',
  danger: '#D98B82',
  success: '#7BA06E',
  badgeSandBg: '#3A2A1F',
  focusRing: 'focus-visible:ring-[#F3E7D8] focus-visible:ring-offset-[#20130D]',
  cardClass: 'border rounded-lg p-3',
  cardBg: CONSOLE_SURFACE,
  switchOn: '#F3E7D8',
  switchOff: '#4A382F',
  switchKnob: '#1B0F0A',
  spinnerTrack: '#4A382F',
  spinnerHead: '#F3E7D8',
  chart: {
    axis: '#B0998A',
    grid: '#4A382F',
    tooltipBg: '#20130D',
    tooltipBorder: '#4A382F',
    tooltipText: '#F3E7D8',
  },
};

export const THEME_TOKENS = { portal: PORTAL, console: CONSOLE };

const ThemeContext = createContext('portal');

/**
 * Wrap a subtree so its primitives render the given theme.
 * @param {{ theme?: 'portal'|'console', children: React.ReactNode }} props
 */
export function ThemeProvider({ theme = 'portal', children }) {
  const resolved = theme === 'console' ? 'console' : 'portal';
  return <ThemeContext.Provider value={resolved}>{children}</ThemeContext.Provider>;
}

/** @returns {'portal'|'console'} the current theme (default 'portal'). */
export function useTheme() {
  return useContext(ThemeContext);
}

/** @returns the resolved token bag for the current theme. */
export function useTokens() {
  return THEME_TOKENS[useTheme()] || THEME_TOKENS.portal;
}
